import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, FileText, Send, Check, X, NotebookPen, Download, History, Shield, Trash2, MessageSquare, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatET, formatDate } from "@/lib/api";
import MobileShell from "@/components/admin/MobileShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState(null); // { past, upcoming, is_purged }
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [fee, setFee] = useState(50);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    try {
      const [{ data: d }, { data: m }] = await Promise.all([
        api.get(`/admin/clients/${id}`),
        api.get(`/admin/clients/${id}/messages`),
      ]);
      setData(d);
      setMessages(m);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.info("Client no longer exists.");
        nav("/admin", { replace: true });
        return;
      }
      toast.error("Failed to load client.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <MobileShell title="Client"><div className="text-slate-500 text-sm">Loading…</div></MobileShell>;

  const latestContract = (data.contracts || [])[0];

  const completeAppt = async () => {
    const appt = (data.appointments || []).find((a) => a.status === "scheduled");
    if (!appt) return toast.info("No upcoming consultation to mark complete.");
    await api.post(`/admin/appointments/${appt.appointment_id}/complete`);
    toast.success("Marked complete.");
    load();
  };

  const sendContract = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.post("/admin/contracts/send", { client_id: id, fee_percentage: Number(fee) });
      toast.success("Contract sent.");
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send.");
    } finally { setLoading(false); }
  };

  const resendContract = async () => {
    if (!latestContract) return;
    await api.post(`/admin/contracts/${latestContract.contract_id}/resend`);
    toast.success("Reminder sent.");
    load();
  };

  const markNotInterested = async () => {
    await api.post(`/admin/clients/${id}/not-interested`);
    toast.success("Marked not interested.");
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/admin/clients/${id}/notes`, { body: noteText });
    setNoteText(""); setNoteOpen(false);
    toast.success("Note saved.");
    load();
  };

  const purgeClient = async () => {
    setPurging(true);
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success("Client permanently deleted.");
      nav("/admin", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete client.");
      setPurging(false);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const isPurged = data?.status === "purged" || messages?.is_purged;

  return (
    <MobileShell title={data.full_name}>
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 inline-flex items-center gap-1 mb-2" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isPurged && (
        <div className="mb-3 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800" data-testid="purged-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <span className="font-medium">Client purged.</span> All scheduled appointments were cancelled and no further texts or emails will be sent.
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-3xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-xl text-[#0A1930] tracking-tight">{data.full_name}</h1>
            <p className="text-xs text-slate-500 mt-0.5">{data.branch} · {data.current_rating || "No current rating"}</p>
          </div>
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{data.status}</span>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <a href={`mailto:${data.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5" data-testid="email-link">
            <Mail className="w-3.5 h-3.5" /> {data.email}
          </a>
          <a href={`tel:${data.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5" data-testid="phone-link">
            <Phone className="w-3.5 h-3.5" /> {data.phone}
          </a>
        </div>

        {data.appointments?.[0] && (
          <div className="mt-4 rounded-2xl bg-[#F5F5F7] p-3 text-sm text-[#0A1930]">
            <span className="text-[11px] tracking-[0.15em] uppercase text-slate-500">Consultation</span>
            <p className="mt-1">{formatET(data.appointments[0].date, data.appointments[0].time)} · {data.appointments[0].status}</p>
          </div>
        )}
        {data.help_description && (
          <p className="mt-4 text-sm text-slate-600 leading-relaxed bg-[#F5F5F7] rounded-2xl p-3">"{data.help_description}"</p>
        )}
      </div>

      {/* Quick action grid */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Action onClick={completeAppt} icon={Check} label="Mark complete" testid="action-complete" disabled={isPurged} />
        <Action onClick={() => setSendOpen(true)} icon={Send} label="Send contract" testid="action-send-contract" primary disabled={isPurged} />
        <Action onClick={resendContract} icon={History} label="Resend contract" disabled={!latestContract || isPurged} testid="action-resend" />
        <Action onClick={() => setNoteOpen(true)} icon={NotebookPen} label="Add note" testid="action-add-note" />
        <Action onClick={markNotInterested} icon={X} label="Not interested" testid="action-not-interested" disabled={isPurged} />
        {latestContract?.status === "signed" && (
          <Action as="a" href={`${apiBase}/api/contracts/${latestContract.contract_id}/pdf`} target="_blank" rel="noreferrer" icon={Download} label="View signed PDF" testid="action-view-pdf" />
        )}
        {!isPurged && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="col-span-2 rounded-2xl px-3 py-3 text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="action-purge"
              >
                <Trash2 className="w-4 h-4" /> <span>Delete client permanently</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently delete {data.full_name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This <span className="font-semibold">cannot be undone</span>. The client and every related record — appointments, notes, contracts, text logs, email logs, and audit entries scoped to them — will be wiped from the system. They will no longer appear anywhere.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={purgeClient}
                  disabled={purging}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="purge-confirm"
                >
                  {purging ? "Deleting…" : "Yes, delete permanently"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Contracts */}
      <Section title="Contracts" testid="section-contracts">
        {(data.contracts || []).length === 0 && <p className="text-sm text-slate-500">No contracts yet.</p>}
        {(data.contracts || []).map((c) => (
          <div key={c.contract_id} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#0A1930]">{c.fee_percentage}% success fee</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{c.status}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Sent {formatDate(c.created_at)} · Expires {formatDate(c.expires_at)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={`/sign/${c.token}`} target="_blank" rel="noreferrer" className="text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50" data-testid={`contract-sign-link-${c.contract_id}`}>Signing page</a>
              {c.status === "signed" && (
                <a href={`${apiBase}/api/contracts/${c.contract_id}/pdf`} target="_blank" rel="noreferrer" className="text-xs rounded-full bg-[#0066CC] text-white px-3 py-1.5">Download PDF</a>
              )}
            </div>
          </div>
        ))}
      </Section>

      {/* Notes */}
      <Section title="Notes" testid="section-notes">
        {(data.notes || []).length === 0 && <p className="text-sm text-slate-500">No notes yet.</p>}
        {(data.notes || []).map((n) => (
          <div key={n.note_id} className="bg-white border border-slate-200 rounded-2xl p-4">
            <p className="text-sm text-[#0A1930] leading-relaxed">{n.body}</p>
            <p className="text-[11px] text-slate-400 mt-2">{n.author} · {formatDate(n.created_at)}</p>
          </div>
        ))}
      </Section>

      {/* Messages & reminders */}
      <Section title="Messages & reminders" testid="section-messages">
        {/* Upcoming */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] tracking-[0.14em] uppercase text-slate-500 mb-2">Next scheduled texts</p>
          {isPurged ? (
            <p className="text-sm text-slate-500">All future texts cancelled (client purged).</p>
          ) : (messages?.upcoming?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500">No upcoming texts scheduled.</p>
          ) : (
            <ul className="space-y-2" data-testid="upcoming-list">
              {messages.upcoming.map((u, i) => (
                <li key={`${u.appointment_id}-${u.kind}-${i}`} className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-2">
                  <Clock className="w-4 h-4 mt-0.5 text-[#0066CC] shrink-0" />
                  <div className="text-sm flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[#0A1930] font-medium">{reminderLabel(u)}</span>
                      <span className="text-[10px] tracking-wide rounded-full bg-white border border-slate-200 px-1.5 py-0.5 text-slate-600">{u.audience}</span>
                    </div>
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate">to {u.to || "—"}</p>
                    <p className="text-[12px] text-slate-500 mt-0.5">Fires {formatDate(u.fire_at)} ET</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Past SMS */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] tracking-[0.14em] uppercase text-slate-500 mb-2">Texts already sent</p>
          {(messages?.past?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500">No texts sent yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="past-sms-list">
              {messages.past.map((m) => (
                <li key={m.log_id} className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-2">
                  <MessageSquare className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                  <div className="text-sm flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[#0A1930] font-medium">{kindLabel(m.kind)}</span>
                      <StatusPill status={m.status} />
                    </div>
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate">to {m.to}</p>
                    <p className="text-[12px] text-slate-600 mt-1 leading-snug line-clamp-2">{m.body}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{formatDate(m.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      {/* Email log (admin-internal, currently in text-only mode) */}
      {(data.emails || []).length > 0 && (
        <Section title="Email log" testid="section-emails">
          {data.emails.slice(0, 10).map((m) => (
            <div key={m.log_id} className="bg-white border border-slate-200 rounded-2xl p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Email · {m.status}</span>
                <span className="text-[11px] text-slate-400">{formatDate(m.created_at)}</span>
              </div>
              <p className="mt-1 text-[#0A1930] truncate">{m.subject}</p>
            </div>
          ))}
        </Section>
      )}

      {/* Audit */}
      <Section title="Audit log" testid="section-audit">
        {(data.audit || []).length === 0 && <p className="text-sm text-slate-500">No activity yet.</p>}
        {(data.audit || []).map((a) => (
          <div key={a.log_id} className="text-xs text-slate-600 bg-white border border-slate-200 rounded-2xl p-3 flex items-center justify-between">
            <span><Shield className="inline w-3 h-3 mr-1 text-slate-400" />{a.action} <span className="text-slate-400">by {a.actor}</span></span>
            <span className="text-slate-400">{formatDate(a.created_at)}</span>
          </div>
        ))}
      </Section>

      {/* Note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a note</DialogTitle>
            <DialogDescription>Private to your admin team.</DialogDescription>
          </DialogHeader>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={5} placeholder="What happened?" data-testid="note-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={addNote} data-testid="note-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send contract dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send service agreement</DialogTitle>
            <DialogDescription>
              The client will receive a secure signing link via email and text. Expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.12em] text-slate-500">Success fee (%)</label>
            <input type="number" min={0} max={100} value={fee} onChange={(e) => setFee(e.target.value)} className="w-full h-11 rounded-xl border border-slate-200 px-3" data-testid="contract-fee-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={sendContract} disabled={loading} data-testid="contract-send-confirm">Send contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="mt-6" data-testid={testid}>
      <h2 className="text-[11px] tracking-[0.18em] uppercase text-slate-500 mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Action({ onClick, icon: Icon, label, primary = false, disabled = false, testid, as = "button", ...rest }) {
  const cls = `rounded-2xl px-3 py-3 text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]
    ${primary ? "bg-[#0066CC] text-white hover:bg-[#0055AA]" : "bg-white border border-slate-200 text-[#0A1930] hover:bg-slate-50"}
    ${disabled ? "opacity-50 cursor-not-allowed" : ""}`;
  const Cmp = as;
  return (
    <Cmp onClick={disabled ? undefined : onClick} className={cls} disabled={disabled} data-testid={testid} {...rest}>
      <Icon className="w-4 h-4" /> <span>{label}</span>
    </Cmp>
  );
}

// ── helpers for the Messages & reminders panel ───────────────────────────────
function kindLabel(kind) {
  if (!kind) return "SMS";
  const map = {
    booking_client_confirm: "Booking confirmation (client)",
    booking_admin: "Booking alert (admin)",
    contract_send: "Contract — sent",
    contract_resend: "Contract — resent",
    contract_signed_client: "Contract signed — confirmation",
    contract_signed_admin: "Contract signed — admin alert",
  };
  if (map[kind]) return map[kind];
  const m = kind.match(/^reminder_(client|admin)_(\d+)h$/);
  if (m) return `${m[1] === "client" ? "Client" : "Admin"} reminder · ${m[2]}h before`;
  return kind;
}

function reminderLabel(u) {
  return `${u.audience === "client" ? "Client" : "Admin"} reminder · ${u.offset_hours}h before`;
}

function StatusPill({ status }) {
  const tone = status === "sent"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "failed" || status === "error"
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : status?.startsWith("skipped")
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`text-[10px] tracking-wide rounded-full border px-1.5 py-0.5 ${tone}`}>{status || "—"}</span>
  );
}
